import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Pagination,
  Tooltip,
  Chip,
  Divider,
  alpha,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Collapse,
  useTheme,
  Tabs,
  Tab,
  Popover,
  LinearProgress,
  Backdrop
} from '@mui/material';
import {
  Search,
  Edit,
  Save,
  Close,
  FilterList,
  Sort,
  Image as ImageIcon,
  Refresh,
  NoteAlt,
  AutoFixHigh,
  Visibility,
  VisibilityOff,
  ChevronRight,
  ExpandMore,
  AddPhotoAlternate,
  Settings,
  ViewCompact,
  TextFields,
  Link as LinkIcon,
  LocalOffer,
  CalendarToday,
  Person,
  Share,
  ThumbUp,
  ChatBubbleOutline,
  Bookmark,
  PlaylistPlay as PlaylistPlayIcon
} from '@mui/icons-material';
import { RefreshCw as RefreshCwIcon, FileEdit as FileEditIcon } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import apiClient from '../../../utils/apiUtils';
import WordPressClient from '../../../utils/WordPressClient';
import aiClient from '../../../api/clients/AiClient';
import { formatContent } from '../../../utils/contentFormatting';
import PageHeader from '../../PageHeader';
import ImageControls from '../../ImageControls';
import { useAIModels } from '../../../hooks/useAIModels';
import { useSnackbar } from 'notistack';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ImageSelectionDialog from './ImageSelectionDialog';

// Add this custom hook before the ArticleRewritesPage component
const useSafeSnackbar = () => {
  try {
    const snackbar = useSnackbar();
    if (!snackbar) {
      throw new Error('Snackbar context not available');
    }
    return snackbar;
  } catch (error) {
    console.warn('Snackbar context not available:', error);
    return {
      enqueueSnackbar: (message, options) => {
        console.log('Snackbar message:', message, options);
        // Fallback to console.log if snackbar is not available
        if (typeof message === 'string') {
          console.log(message);
        }
      }
    };
  }
};

const ArticleRewritesPage = ({ onGeneratingChange }) => {
  const theme = useTheme();
  const { authenticatedRequest } = useAuth();
  const { models: availableModels, loading: modelsLoading, error: modelsError } = useAIModels(authenticatedRequest);
  const [model, setModel] = useState('claude-3-sonnet@20240229');
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [posts, setPosts] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [isGeneratingTitle, setIsGeneratingTitle] = useState(false);
  const [isGeneratingContent, setIsGeneratingContent] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [expandedSections, setExpandedSections] = useState({
    content: true,
    seo: false,
    media: false,
    settings: false
  });
  const [sectionToRewrite, setSectionToRewrite] = useState(null); // Keep this to track *if* a section is selected
  const [originalSectionHtml, setOriginalSectionHtml] = useState(''); // Store original HTML
  const [isRewritingSection, setIsRewritingSection] = useState(false);
  const [titleSuggestions, setTitleSuggestions] = useState([]);
  const [showTitleSuggestions, setShowTitleSuggestions] = useState(false);
  const [titleStyle, setTitleStyle] = useState('seo');
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [originalTitle, setOriginalTitle] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [rewrittenSectionHtml, setRewrittenSectionHtml] = useState(''); // Store rewritten HTML
  const [selectedSectionId, setSelectedSectionId] = useState(null); // Store ID instead of element
  // --- NEW Metadata State ---
  const [editSlug, setEditSlug] = useState('');
  const [editExcerpt, setEditExcerpt] = useState('');
  const [editSeoTitle, setEditSeoTitle] = useState('');
  const [editMetaDescription, setEditMetaDescription] = useState('');
  const [originalSlug, setOriginalSlug] = useState('');
  const [originalExcerpt, setOriginalExcerpt] = useState('');
  const [originalSeoTitle, setOriginalSeoTitle] = useState('');
  const [originalMetaDescription, setOriginalMetaDescription] = useState('');
  // --- End Metadata State ---
  const articleContentRef = useRef(null);
  
  // --- NEW State for Inline Editing ---
  const [inlineEditorHtml, setInlineEditorHtml] = useState(null); // Initialize to null
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempEditTitle, setTempEditTitle] = useState(''); // For inline title editing cancel
  // --- NEW Popover State ---
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  
  // --- NEW State for Batch Rewriting ---
  const [batchSelectedSectionIds, setBatchSelectedSectionIds] = useState([]);
  const [isBatchRewriting, setIsBatchRewriting] = useState(false);
  const [batchRewriteProgress, setBatchRewriteProgress] = useState(0);
  const [batchRewriteStatusMessage, setBatchRewriteStatusMessage] = useState('');
  
  // --- Image Replacement State ---
  const [selectedImageElementId, setSelectedImageElementId] = useState(null);
  const [selectedImageSrc, setSelectedImageSrc] = useState(null);
  
  // Move the snackbar hook inside the component
  const snackbar = useSafeSnackbar();
  
  // Use a simple alert function that falls back to window.alert if snackbar fails
  const showAlert = (message, variant) => {
    try {
      if (snackbar && snackbar.enqueueSnackbar) {
        snackbar.enqueueSnackbar(message, { variant });
      } else {
        // Fallback to regular alert if snackbar is not available
        alert(message);
      }
    } catch (err) {
      console.error("Snackbar error:", err);
      alert(message);
    }
  };

  const quillRef = useRef(null); // NEW: Ref for Quill instance
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false); // NEW: State for Image Dialog
  const clickTimerRef = useRef(null); // NEW: Ref for click timer

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedSite) {
      fetchCategories();
      fetchPosts();
    }
  }, [selectedSite, selectedCategory, searchQuery, currentPage]);

  useEffect(() => {
    const initializeAI = async () => {
      if (availableModels.length > 0) {
        try {
          const keys = await authenticatedRequest(() => apiClient.getApiKeys());
          
          // Initialize AI client with the API keys and model configs
          aiClient.setApiKeys(keys);
          aiClient.updateModelConfigs(availableModels);
          
          console.log('AI client initialized with models:', availableModels.map(m => m.value));
          
          // Set initial model if none selected
          if (!model && availableModels.length > 0) {
            const defaultModel = availableModels[0].value;
            console.log('Setting default model:', defaultModel);
            setModel(defaultModel);
          }
        } catch (error) {
          console.error('Error initializing AI client:', error);
          setError('Failed to initialize AI services: ' + error.message);
        }
      }
    };

    initializeAI();
  }, [availableModels, authenticatedRequest]);

  const fetchInitialData = async () => {
    try {
      const sitesData = await authenticatedRequest(() => apiClient.getWordPressSites());
      setSites(sitesData);
      if (sitesData.length > 0) {
        setSelectedSite(sitesData[0].id);
      }
    } catch (err) {
      setError('Failed to load WordPress sites');
    }
  };

  const fetchCategories = async () => {
    try {
      const categoriesData = await authenticatedRequest(() => 
        apiClient.getCategories(selectedSite)
      );
      setCategories(categoriesData);
    } catch (err) {
      setError('Failed to load categories');
    }
  };

  const fetchPosts = async () => {
    if (!selectedSite) return;
    
    setLoading(true);
    try {
      const site = sites.find(s => s.id === selectedSite);
      if (!site) throw new Error('Selected site not found');
      
      const wpClient = new WordPressClient(site);
      const credentials = await authenticatedRequest(() => 
        apiClient.getWordPressCredentials(selectedSite)
      );
      wpClient.setCredentials(credentials.username, credentials.password);
      
      const { posts, totalPages } = await wpClient.getPosts({
        page: currentPage,
        perPage: 10,
        search: searchQuery,
        category: selectedCategory,
        _embed: true
      });
      
      // Process posts and fetch missing media if needed
      const postsWithMedia = await Promise.all(posts.map(async post => {
        let post_with_image = {...post};
        
        console.log('Processing post:', post.id, {
          title: post.title.rendered,
          has_featured_media: !!post.featured_media,
          embedded: post._embedded ? Object.keys(post._embedded) : 'no _embedded'
        });
        
        // Try to get image from _embedded data first
        if (post._embedded && post._embedded['wp:featuredmedia'] && post._embedded['wp:featuredmedia'][0]) {
          if (post._embedded['wp:featuredmedia'][0].source_url) {
            post_with_image.featured_media_url = post._embedded['wp:featuredmedia'][0].source_url;
          } else if (post._embedded['wp:featuredmedia'][0].media_details && 
                    post._embedded['wp:featuredmedia'][0].media_details.sizes) {
            const sizes = post._embedded['wp:featuredmedia'][0].media_details.sizes;
            if (sizes.medium) {
              post_with_image.featured_media_url = sizes.medium.source_url;
            } else if (sizes.full) {
              post_with_image.featured_media_url = sizes.full.source_url;
            }
          } else if (post._embedded['wp:featuredmedia'][0].guid && 
                    post._embedded['wp:featuredmedia'][0].guid.rendered) {
            post_with_image.featured_media_url = post._embedded['wp:featuredmedia'][0].guid.rendered;
          }
        }
        
        // If no embedded media but post has featured_media ID, fetch it directly
        if (!post_with_image.featured_media_url && post.featured_media) {
          try {
            console.log(`Post ${post.id} has featured_media ID but no embedded data, fetching image URL directly`);
            const mediaUrl = await wpClient.getMediaUrl(post.featured_media);
            if (mediaUrl) {
              post_with_image.featured_media_url = mediaUrl;
              console.log(`Successfully fetched media URL for post ${post.id}:`, mediaUrl);
            }
          } catch (err) {
            console.error(`Failed to fetch media for post ${post.id}:`, err);
          }
        }
        
        console.log(`Post ${post.id} final image result:`, post_with_image.featured_media_url || 'No image found');
        return post_with_image;
      }));
      
      setPosts(postsWithMedia);
      setTotalPages(totalPages);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPost = async (post) => {
    setSelectedPost(post);
    setEditTitle(post.title.rendered);
    // Ensure IDs on initial load
    const initialContentWithIds = ensureStableIds(post.content.rendered);
    const initialContentWithImageIds = ensureImageIds(initialContentWithIds);
    setEditContent(initialContentWithImageIds);
    setOriginalTitle(post.title.rendered);
    setOriginalContent(initialContentWithImageIds); // Store original with IDs too

    // --- Load Metadata ---
    const currentSlug = post.slug || '';
    // Extract raw excerpt if possible, otherwise use rendered and strip tags
    const currentExcerpt = post.excerpt?.raw || post.excerpt?.rendered?.replace(/<[^>]*>/g, '') || '';
    // Attempt to get common SEO meta fields (Yoast example)
    const currentSeoTitle = post.meta?._yoast_wpseo_title || ''; 
    const currentMetaDesc = post.meta?._yoast_wpseo_metadesc || '';
    
    setEditSlug(currentSlug);
    setEditExcerpt(currentExcerpt);
    setEditSeoTitle(currentSeoTitle);
    setEditMetaDescription(currentMetaDesc);

    setOriginalSlug(currentSlug);
    setOriginalExcerpt(currentExcerpt);
    setOriginalSeoTitle(currentSeoTitle);
    setOriginalMetaDescription(currentMetaDesc);
    // --- End Load Metadata ---

    // Reset other states
    setSectionToRewrite(null);
    setOriginalSectionHtml('');
    setSelectedSectionId(null);
    setInlineEditorHtml(null);
    setIsEditingTitle(false);
    setPopoverAnchorEl(null);
    setIsPopoverOpen(false);
    setBatchSelectedSectionIds([]);
    setIsBatchRewriting(false);
    setSelectedImageElementId(null); // Reset image selection
    setSelectedImageSrc(null);
    
    setTimeout(() => {
        document.querySelectorAll('.selected, .batch-selected, .selected-image').forEach(el => {
            el.classList.remove('selected', 'batch-selected', 'selected-image');
        });
    }, 0);
  };

  const getAuxiliaryModel = () => {
    const stored = localStorage.getItem('globalAiModel');
    if (stored && availableModels.some(m => m.value === stored)) {
      return stored;
    }
    return availableModels[0]?.value || 'claude-3-sonnet@20240229';
  };

  const handleGenerateTitle = async () => {
    if (!editContent) return;
    
    setIsGeneratingTitle(true);
    setShowTitleSuggestions(true);
    try {
      const auxiliaryModelId = getAuxiliaryModel();
      console.log("Using auxiliary model for title generation:", auxiliaryModelId);
      
      if (!auxiliaryModelId) {
        throw new Error('No AI model available. Please check your settings.');
      }
      
      // Create prompt based on selected style
      let styleInstructions = '';
      switch(titleStyle) {
        case 'question':
          styleInstructions = 'Format all titles as engaging questions that would make readers curious.';
          break;
        case 'list':
          styleInstructions = 'Format all titles as numbered lists or "X ways/tips/strategies" style.';
          break;
        case 'howto':
          styleInstructions = 'Format all titles as "How to" guides that promise practical solutions.';
          break;
        case 'seo':
        default:
          styleInstructions = 'Optimize all titles for search engines with relevant keywords while keeping them engaging.';
      }
      
      const titleSuggestionsResponse = await aiClient.generateArticleContent({
        title: `Generate 20 ${titleStyle} titles for this article`,
        modelId: auxiliaryModelId,
        customPrompt: `Based on this article content, generate 20 compelling titles that are engaging and click-worthy. ${styleInstructions} The titles should be between 50-70 characters long and include relevant keywords.

Article content:
${editContent.substring(0, 2000)} // Limit content to prevent token limits

Generate exactly 20 distinct titles, numbered from 1-20, one per line. Be creative and diverse in your suggestions.`
      });

      // Extract titles and clean them up
      const titles = titleSuggestionsResponse
        .split('\n')
        .filter(line => line.trim())
        .map(line => {
          // Remove the numbering at the beginning (e.g., "1. ", "2. ")
          let cleanTitle = line.replace(/^\d+[\.\)]\s*/, '').trim();
          // Remove quotes around titles if present
          cleanTitle = cleanTitle.replace(/^["'](.*)["']$/, '$1').trim();
          return cleanTitle;
        })
        .filter(title => title.length > 0);
      
      setTitleSuggestions(titles);
      
      // Don't auto-select a title anymore, let the user choose
      console.log(`Generated ${titles.length} title suggestions`);
    } catch (err) {
      console.error("Title generation error:", err);
      setError('Failed to generate title suggestions: ' + err.message);
      setShowTitleSuggestions(false);
    } finally {
      setIsGeneratingTitle(false);
    }
  };

  const selectTitleSuggestion = (title) => {
    setEditTitle(title);
  };

  const handleRewriteContent = async () => {
    if (!editContent) return;
    
    setIsGeneratingContent(true);
    try {
      const auxiliaryModelId = getAuxiliaryModel();
      console.log("Using auxiliary model for content rewrite:", auxiliaryModelId);
      
      if (!auxiliaryModelId) {
        throw new Error('No AI model available. Please check your settings.');
      }
      
      const rewrittenContent = await aiClient.generateArticleContent({
        title: editTitle,
        modelId: auxiliaryModelId,
        customPrompt: `Rewrite this article in a fresh, unique way while maintaining the same information and structure. Make it more engaging and SEO-friendly.

Original content:
${editContent}

Rewrite the content, maintaining the same structure but with fresh wording and improved readability.`
      });

      setEditContent(formatContent(rewrittenContent));
    } catch (err) {
      console.error("Content rewrite error:", err);
      setError('Failed to rewrite content: ' + err.message);
    } finally {
      setIsGeneratingContent(false);
    }
  };

  const handleResetArticle = () => {
    if (window.confirm('Reset article to original? All unsaved changes will be lost.')) {
      setEditTitle(originalTitle);
      setEditContent(originalContent);
      setSectionToRewrite(null);
      setOriginalSectionHtml('');
      setShowTitleSuggestions(false);
      setBatchSelectedSectionIds([]);
      setIsBatchRewriting(false);
      handleClosePopover();
      document.querySelectorAll('.selected, .batch-selected, .rewritten').forEach(el => {
        el.classList.remove('selected', 'batch-selected', 'rewritten');
      });
    }
  };

  const handleSaveConfirm = () => {
    setSaveConfirmOpen(true);
  };

  const handleSavePost = async () => {
    if (!selectedPost) return;
    
    setLoading(true);
    setSaveConfirmOpen(false);
    
    try {
      const site = sites.find(s => s.id === selectedSite);
      if (!site) throw new Error('Selected site not found');
      
      const wpClient = new WordPressClient(site);
      const credentials = await authenticatedRequest(() => 
        apiClient.getWordPressCredentials(selectedSite)
      );
      wpClient.setCredentials(credentials.username, credentials.password);
      
      await wpClient.updatePost(selectedPost.id, {
        title: editTitle,
        content: editContent,
        // --- Add Metadata Fields ---
        slug: editSlug,
        excerpt: editExcerpt,
        meta: {
          // Include existing meta and update SEO fields
          // Be cautious not to overwrite other important meta fields
          ...(selectedPost.meta || {}),
          _yoast_wpseo_title: editSeoTitle, // Example key for Yoast
          _yoast_wpseo_metadesc: editMetaDescription // Example key for Yoast
          // Add other SEO plugin keys here if needed
        }
        // --- End Add Metadata Fields ---
      });
      
      const updatedPosts = posts.map(p => 
        p.id === selectedPost.id 
          ? {...p, title: {rendered: editTitle}, content: {rendered: editContent}} 
          : p
      );
      setPosts(updatedPosts);
      
      setOriginalTitle(editTitle);
      setOriginalContent(editContent);
      
      setError(null);
      snackbar.enqueueSnackbar('Post updated successfully!', { variant: 'success' });
    } catch (err) {
      setError('Failed to update post');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSectionSelect = (event, sectionHtml, sectionId) => {
    // This function now *only* handles opening the popover for single edit
    // It assumes batch selection is already cleared before calling it.

    // Remove selection from any previously selected element (redundant? maybe keep)
    document.querySelectorAll('.selected').forEach(el => {
      el.classList.remove('selected');
    });

    // --- DEBUG LOG --- 
    console.log(`Setting inlineEditorHtml for ID ${sectionId} with HTML:`, sectionHtml);
    
    setSelectedSectionId(sectionId);
    setOriginalSectionHtml(sectionHtml);
    setInlineEditorHtml(sectionHtml); // Set the state
    setIsEditingTitle(false);

    // Set Popover State
    // Use event.currentTarget IF passed directly from double-click handler
    // If event isn't available or reliable, find element by ID
    const anchorElement = document.getElementById(sectionId) || (event ? event.currentTarget : null);
    setPopoverAnchorEl(anchorElement); 
    setIsPopoverOpen(true);

    console.log("Selected section for Popover editor:", { id: sectionId, element: anchorElement });
  };

  const handleClosePopover = () => {
    if (selectedSectionId) {
      const element = document.getElementById(selectedSectionId);
      if (element) {
        element.classList.remove('selected');
      }
    }
    setSelectedSectionId(null);
    setOriginalSectionHtml('');
    setInlineEditorHtml(null); // Reset to null
    setIsRewritingSection(false);
    setPopoverAnchorEl(null);
    setIsPopoverOpen(false);
  };

  const handleRewriteSection = async () => {
    if (!selectedSectionId || !inlineEditorHtml) return;
    
    setIsRewritingSection(true);
    
    try {
      const auxiliaryModelId = getAuxiliaryModel();
      console.log("Starting section rewrite for section:", selectedSectionId);
      console.log("Section HTML (from inline editor):", inlineEditorHtml);
      console.log("Using auxiliary model:", auxiliaryModelId);
      
      if (!auxiliaryModelId) {
        throw new Error('No AI model available. Please check your settings.');
      }
      
      let contextBeforeHtml = '<!-- No preceding element -->';
      let contextAfterHtml = '<!-- No succeeding element -->';

      try {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = editContent;
        const selectedElement = tempDiv.querySelector(`#${selectedSectionId}`);

        if (selectedElement) {
          const prevElement = selectedElement.previousElementSibling;
          const nextElement = selectedElement.nextElementSibling;

          if (prevElement) {
            contextBeforeHtml = prevElement.outerHTML;
            console.log("Context Before:", contextBeforeHtml.substring(0, 100) + '...'); 
          }
          if (nextElement) {
            contextAfterHtml = nextElement.outerHTML;
            console.log("Context After:", contextAfterHtml.substring(0, 100) + '...');
          }
        } else {
          console.warn("Could not find selected element in full content to get context.");
        }
      } catch (contextError) {
        console.error("Error gathering context:", contextError);
      }
      
      const rewrittenSection = await aiClient.generateArticleContent({
        title: `Rewrite section of "${editTitle}"`,
        modelId: auxiliaryModelId,
        customPrompt: `You are rewriting a specific section of an article. Use the provided context to ensure the rewrite fits naturally. **Rewrite only the "HTML section to rewrite". Do NOT rewrite the "Context Before" or "Context After" sections.** Maintain the same information, intent, and overall HTML structure (e.g., keep headings, paragraphs, lists, bold/italic text) of the section being rewritten. Make it more engaging and SEO-friendly. Return only the valid HTML content for the rewritten section.

Context Before:
\`\`\`html
${contextBeforeHtml}
\`\`\`

HTML section to rewrite:
\`\`\`html
${inlineEditorHtml}
\`\`\`

Context After:
\`\`\`html
${contextAfterHtml}
\`\`\`

Rewrite just the "HTML section to rewrite" with fresh wording and improved readability. Keep a similar length and maintain the same key points. **Return valid HTML content for the rewritten section ONLY.**`
      });

      console.log("Received raw rewritten section:", rewrittenSection);

      let cleanedHtml = rewrittenSection.trim();
      if (cleanedHtml.startsWith('```html')) cleanedHtml = cleanedHtml.substring(7);
      if (cleanedHtml.endsWith('```')) cleanedHtml = cleanedHtml.substring(0, cleanedHtml.length - 3);
      cleanedHtml = cleanedHtml.trim();

      setInlineEditorHtml(cleanedHtml);
      
    } catch (err) {
      console.error("Error rewriting section:", err);
      showAlert('Error rewriting section: ' + err.message, 'error');
    } finally {
      setIsRewritingSection(false);
    }
  };

  const applyInlineSectionChanges = () => {
    console.log("Starting applyInlineSectionChanges...");
    console.log("Selected element ID:", selectedSectionId);
    console.log("New HTML (from inline editor):", inlineEditorHtml);
    console.log("Current editContent length:", editContent.length);

    if (!selectedSectionId || inlineEditorHtml === null) {
      const missing = [];
      if (!selectedSectionId) missing.push('a selected element');
      if (inlineEditorHtml === null) missing.push('content in the editor');
      console.warn(`Cannot apply: missing ${missing.join(' and ')}.`);
      showAlert(`Cannot apply: missing ${missing.join(' and ')}. Please select an element and ensure editor has content.`, "warning");
      return;
    }

    const targetElementId = selectedSectionId;

    try {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = editContent;
      console.log("Created temp div with current content");

      const elementToUpdate = tempDiv.querySelector(`#${targetElementId}`);

      if (elementToUpdate) {
        console.log(`Found element with ID ${targetElementId} in temp DOM. Tag: ${elementToUpdate.tagName}`);

        const tempRewriteDiv = document.createElement('div');
        tempRewriteDiv.innerHTML = inlineEditorHtml; 
        
        const firstRewriteElement = tempRewriteDiv.firstElementChild; 
        if (firstRewriteElement && 
            tempRewriteDiv.childNodes.length === 1 &&
            firstRewriteElement.tagName === elementToUpdate.tagName) {
            console.log('Applying innerHTML from rewritten content because tags match.');
            elementToUpdate.innerHTML = firstRewriteElement.innerHTML;
        } else {
            console.log('Applying full rewritten content to innerHTML because tags differ or structure is complex.');
            elementToUpdate.innerHTML = inlineEditorHtml; 
        }
        
        console.log(`Updated innerHTML of element ${targetElementId}`);

        elementToUpdate.classList.add('rewritten');
        elementToUpdate.classList.remove('selected');

      } else {
        console.error(`Could not find element with ID ${targetElementId} in the current content.`);
        throw new Error(`Failed to find the selected element (ID: ${targetElementId}) in the article content. The content might have changed or the ID is incorrect.`);
      }

      // Ensure text element IDs and image IDs before setting state
      const contentWithTextIds = ensureStableIds(tempDiv.innerHTML);
      const finalContentWithAllIds = ensureImageIds(contentWithTextIds);
      console.log("Final content length (with text/image IDs ensured):", finalContentWithAllIds.length);
      
      setEditContent(finalContentWithAllIds);
      handleClosePopover();
      showAlert('Rewritten section applied successfully!', 'success');

    } catch (error) {
      console.error("Error applying rewritten section:", error);
      showAlert(error.message || 'Error applying rewritten content', 'error');
    }
  };

  const handleBatchRewriteSelectedSections = async () => {
    if (batchSelectedSectionIds.length === 0 || isBatchRewriting) return;

    setIsBatchRewriting(true);
    setBatchRewriteProgress(0);
    setBatchRewriteStatusMessage('Starting batch rewrite...');
    const totalSections = batchSelectedSectionIds.length;

    let currentContent = editContent;

    try {
      for (let i = 0; i < totalSections; i++) {
        const sectionId = batchSelectedSectionIds[i];
        const progress = ((i + 1) / totalSections) * 100;
        setBatchRewriteProgress(progress);
        setBatchRewriteStatusMessage(`Rewriting section ${i + 1} of ${totalSections}...`);
        console.log(`Batch rewriting section ${i + 1}/${totalSections}: ${sectionId}`);

        let sectionHtml = '';
        let contextBeforeHtml = '<!-- No preceding element -->';
        let contextAfterHtml = '<!-- No succeeding element -->';
        let elementTag = 'P';

        try {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = currentContent;
          const elementToRewrite = tempDiv.querySelector(`#${sectionId}`);

          if (!elementToRewrite) {
            console.error(`Batch: Could not find element ${sectionId} in current content. Skipping.`);
            showAlert(`Skipping section ${sectionId}: Not found in content.`, 'warning');
            continue;
          }

          sectionHtml = elementToRewrite.innerHTML;
          elementTag = elementToRewrite.tagName;
          const prevElement = elementToRewrite.previousElementSibling;
          const nextElement = elementToRewrite.nextElementSibling;
          if (prevElement) contextBeforeHtml = prevElement.outerHTML;
          if (nextElement) contextAfterHtml = nextElement.outerHTML;

        } catch (error) {
          console.error(`Batch: Error getting content/context for ${sectionId}:`, error);
          showAlert(`Error preparing section ${sectionId}. Skipping.`, 'error');
          continue;
        }

        let cleanedHtml = '';
        try {
          const auxiliaryModelId = getAuxiliaryModel();
          if (!auxiliaryModelId) throw new Error('No AI model available.');

          const rewrittenSection = await aiClient.generateArticleContent({
             title: `Rewrite section of "${editTitle}"`,
             modelId: auxiliaryModelId,
             customPrompt: `You are rewriting a specific section of an article. Use the provided context to ensure the rewrite fits naturally. **Rewrite only the "HTML section to rewrite". Do NOT rewrite the "Context Before" or "Context After" sections.** Maintain the same information, intent, and overall HTML structure (e.g., keep headings, paragraphs, lists, bold/italic text) of the section being rewritten. Make it more engaging and SEO-friendly. Return only the valid HTML content for the rewritten section.

Context Before:
\`\`\`html
${contextBeforeHtml}
\`\`\`

HTML section to rewrite:
\`\`\`html
<${elementTag.toLowerCase()}>${sectionHtml}</${elementTag.toLowerCase()}>
\`\`\`

Context After:
\`\`\`html
${contextAfterHtml}
\`\`\`

Rewrite just the "HTML section to rewrite" with fresh wording and improved readability. Keep a similar length and maintain the same key points. **Return valid HTML content for the rewritten section ONLY.**`
          });

          cleanedHtml = rewrittenSection.trim();
          if (cleanedHtml.startsWith('```html')) cleanedHtml = cleanedHtml.substring(7);
          if (cleanedHtml.endsWith('```')) cleanedHtml = cleanedHtml.substring(0, cleanedHtml.length - 3);
          cleanedHtml = cleanedHtml.trim();

          if (!cleanedHtml) {
             console.warn(`Batch: AI returned empty content for ${sectionId}. Skipping.`);
             showAlert(`AI returned empty content for ${sectionId}. Skipping.`, 'warning');
             continue;
          }

        } catch (aiError) {
            console.error(`Batch: AI Error rewriting section ${sectionId}:`, aiError);
            showAlert(`AI error rewriting section ${sectionId}: ${aiError.message}. Skipping.`, 'error');
            continue;
        }

        try {
           const tempApplyDiv = document.createElement('div');
           tempApplyDiv.innerHTML = currentContent;
           const elementToUpdate = tempApplyDiv.querySelector(`#${sectionId}`);

           if (!elementToUpdate) {
               console.error(`Batch Apply: Could not find element ${sectionId} again. This shouldn't happen. Skipping.`);
               showAlert(`Error applying changes for section ${sectionId}. Skipping.`, 'error');
               continue;
           }

           const tempRewriteDiv = document.createElement('div');
           tempRewriteDiv.innerHTML = cleanedHtml;
           const firstRewriteElement = tempRewriteDiv.firstElementChild;

           if (firstRewriteElement && tempRewriteDiv.childNodes.length === 1 && firstRewriteElement.tagName === elementToUpdate.tagName) {
               elementToUpdate.innerHTML = firstRewriteElement.innerHTML;
           } else {
               elementToUpdate.innerHTML = cleanedHtml;
           }

           elementToUpdate.classList.add('rewritten');
           elementToUpdate.classList.remove('batch-selected');

           currentContent = ensureStableIds(tempApplyDiv.innerHTML);

        } catch (applyError) {
            console.error(`Batch: Error applying changes for section ${sectionId}:`, applyError);
            showAlert(`Error applying changes for section ${sectionId}. Skipping.`, 'error');
            continue;
        }
      }

      setEditContent(currentContent);
      snackbar.enqueueSnackbar(`Batch rewrite completed for ${totalSections} section(s).`, { variant: 'success' });

    } catch (batchError) {
        console.error("Unexpected error during batch rewrite:", batchError);
        showAlert(`An unexpected error occurred during the batch rewrite: ${batchError.message}`, 'error');
        setEditContent(currentContent);
    } finally {
        setIsBatchRewriting(false);
        setBatchSelectedSectionIds([]);
        setBatchRewriteProgress(0);
        setBatchRewriteStatusMessage('');
        setTimeout(() => {
             document.querySelectorAll('.batch-selected').forEach(el => el.classList.remove('batch-selected'));
        }, 0);
    }
  };

  const renderArticlePreview = () => {
    if (!selectedPost) return null;
    
    console.log("Rendering article preview. Batch selected:", batchSelectedSectionIds);
    
    let featuredImageUrl = null;
    if (selectedPost._embedded && selectedPost._embedded['wp:featuredmedia'] && selectedPost._embedded['wp:featuredmedia'][0]) {
      featuredImageUrl = selectedPost._embedded['wp:featuredmedia'][0].source_url;
    } else if (selectedPost.featured_media_url) {
      featuredImageUrl = selectedPost.featured_media_url;
    } else if (selectedPost.featured_image_url) {
      featuredImageUrl = selectedPost.featured_image_url;
    }

    return (
      <Box sx={{ 
        p: 4,
        maxWidth: '800px',
        mx: 'auto',
        fontFamily: 'Inter, system-ui, sans-serif',
        overflow: 'visible',
      }}>
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h1" 
            sx={{ 
              fontSize: '2.5rem',
              fontWeight: 700,
              lineHeight: 1.2,
              mb: 2,
              color: '#1A2034',
            }}
          >
            {editTitle}
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            color: '#6B7280',
            fontSize: '0.95rem',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person sx={{ fontSize: '1.1rem' }} />
              <span>Admin</span>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarToday sx={{ fontSize: '1.1rem' }} />
              <span>{new Date(selectedPost.date).toLocaleDateString()}</span>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ChatBubbleOutline sx={{ fontSize: '1.1rem' }} />
              <span>Comments</span>
            </Box>
          </Box>
        </Box>

        {featuredImageUrl && (
          <Box sx={{ mb: 4 }}>
            <img 
              src={featuredImageUrl} 
              alt={editTitle}
              style={{
                width: '100%',
                height: 'auto',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              }}
            />
          </Box>
        )}

        <Box 
          className="article-content-container"
          id="article-content-container"
          sx={{ 
            fontSize: '1.1rem',
            lineHeight: 1.8,
            color: '#1A2034',
            '& h2, & h3, & h4, & h5, & h6, & p, & blockquote': {
              padding: '0.5rem',
              borderRadius: '8px',
              position: 'relative',
              transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',
              border: '1px solid transparent',
              '&:hover': {
                backgroundColor: alpha('#6B7280', 0.05),
                cursor: 'pointer',
                borderColor: alpha('#6B7280', 0.15),
              },
              '&.selected': {
                backgroundColor: alpha('#3B82F6', 0.1),
                outline: `2px solid ${alpha('#3B82F6', 0.5)}`,
                outlineOffset: '1px',
              },
              '&.batch-selected': {
                 backgroundColor: alpha('#10B981', 0.1),
                 outline: `2px dashed ${alpha('#10B981', 0.6)}`,
                 outlineOffset: '1px',
              },
              '&.rewritten': {
                backgroundColor: alpha('#10B981', 0.05),
                animation: 'fadeInRewritten 0.8s ease-in-out',
              }
            },
            '& h2': { fontSize: '1.8rem', fontWeight: 600, mt: 4, mb: 2 },
            '& p': { mb: 2 },
            '& img': {
              maxWidth: '100%',
              height: 'auto',
              borderRadius: '8px',
              my: 2,
               cursor: 'pointer', // Make images clickable
               border: '3px solid transparent', // Base border
               transition: 'border-color 0.2s ease-in-out'
            },
            '& img.selected-image': { // Style for selected image
               borderColor: alpha('#f59e0b', 0.8), 
               boxShadow: `0 0 10px ${alpha('#f59e0b', 0.5)}`
            },
            '& blockquote': { borderLeft: '4px solid #E04C16', pl: 2, py: 1, my: 2, fontStyle: 'italic', color: '#4B5563' },
            '@keyframes fadeInRewritten': {
              '0%': { backgroundColor: alpha('#10B981', 0.2) },
              '100%': { backgroundColor: alpha('#10B981', 0.05) }
            }
          }}
          onClick={(e) => {
            // Clear any previous click timer
            if (clickTimerRef.current) {
              clearTimeout(clickTimerRef.current);
              clickTimerRef.current = null;
            }

            const clickedTextElement = e.target.closest('h2, h3, h4, h5, h6, p, blockquote');
            const clickedImageElement = e.target.closest('img');

            if (clickedImageElement && clickedImageElement.closest('.article-content-container')) {
                // --- Handle Image Click --- 
                e.stopPropagation();
                if (!clickedImageElement.id) {
                   console.warn("Clicked image missing ID:", clickedImageElement);
                   showAlert("Clicked image is missing an ID. Cannot select.", "warning");
                   return;
                }
                console.log('Image clicked:', clickedImageElement.id);
                
                // Clear text selections
                document.querySelectorAll('.selected, .batch-selected').forEach(el => el.classList.remove('selected', 'batch-selected'));
                setBatchSelectedSectionIds([]);
                setSelectedSectionId(null);
                if (isPopoverOpen) handleClosePopover(); // Close text editor if open

                // Handle image selection/deselection
                const currentSelectedImageId = selectedImageElementId; // Get state before update
                const previouslySelectedImage = currentSelectedImageId ? document.getElementById(currentSelectedImageId) : null;
                if (previouslySelectedImage) {
                  previouslySelectedImage.classList.remove('selected-image');
                }

                if (currentSelectedImageId === clickedImageElement.id) {
                  // Clicked the same image again: Deselect
                  setSelectedImageElementId(null);
                  setSelectedImageSrc(null);
                  console.log('Image deselected');
                } else {
                  // Clicked a new image: Select
                  setSelectedImageElementId(clickedImageElement.id);
                  setSelectedImageSrc(clickedImageElement.src);
                  clickedImageElement.classList.add('selected-image');
                  console.log('Image selected');
                }
                
            } else if (clickedTextElement) {
                // --- Handle Text Element Click (Delayed Batch Toggle) ---
                e.stopPropagation(); 
                if (!clickedTextElement.id) {
                   console.warn("Clicked text element missing ID:", clickedTextElement);
                   showAlert("Clicked section is missing an ID. Cannot select.", "error");
                   return;
                }
                const elementId = clickedTextElement.id;

                // Clear image selection if a text element is clicked
                if (selectedImageElementId) {
                    const prevImg = document.getElementById(selectedImageElementId);
                    if (prevImg) prevImg.classList.remove('selected-image');
                    setSelectedImageElementId(null);
                    setSelectedImageSrc(null);
                }
                 // Also clear single text selection state (visuals cleared below)
                setSelectedSectionId(null);

                // Set a timer to handle batch toggle
                clickTimerRef.current = setTimeout(() => {
                  console.log('Executing delayed single click for text:', elementId);
                  
                  // --- FIX: Check for Shift key --- 
                  if (e.shiftKey) { 
                    // Remove other batch selections before toggling
                    // document.querySelectorAll('.batch-selected').forEach(el => { // <-- REMOVE THIS LINE
                    //     if(el.id !== elementId) el.classList.remove('.batch-selected');
                    // });
                    // Toggle Batch Selection for the clicked element
                    setBatchSelectedSectionIds(prevIds => {
                      const element = document.getElementById(elementId);
                      if (!element) return prevIds; // Element might have disappeared

                      const isCurrentlySelected = prevIds.includes(elementId);
                      if (isCurrentlySelected) {
                          element.classList.remove('batch-selected');
                          return prevIds.filter(id => id !== elementId);
                } else {
                          // Remove other selections before adding this one // <-- REMOVE THIS COMMENT / LOGIC
                          element.classList.add('batch-selected');
                          element.classList.remove('selected'); // Ensure single select class is removed
                          // If allowing multiple: return [...prevIds.filter(id => document.getElementById(id)), elementId]; // <-- USE THIS
                          return [...prevIds.filter(id => document.getElementById(id)), elementId]; 
                      }
                    });
                    console.log("Toggled batch selection (Shift+Click) for:", elementId);
                } else {
                    // Optional: Handle regular single click without shift (e.g., do nothing, or clear batch selections)
                    console.log("Regular single click detected (no shift). Doing nothing.");
                    // Uncomment below to clear batch selection on regular click:
                    // document.querySelectorAll('.batch-selected').forEach(el => el.classList.remove('batch-selected'));
                    // setBatchSelectedSectionIds([]);
                  }
                  clickTimerRef.current = null; 
                }, 250); 
                } else {
                // --- Clicked Background --- 
                // Clear all selections
                 document.querySelectorAll('.selected, .batch-selected, .selected-image').forEach(el => {
                    el.classList.remove('selected', 'batch-selected', 'selected-image');
                 });
                setSelectedSectionId(null);
                setBatchSelectedSectionIds([]);
                setSelectedImageElementId(null);
                setSelectedImageSrc(null);
                if (isPopoverOpen) handleClosePopover();
            }
          }}
          onDoubleClick={(e) => { 
              // Clear the single click timer immediately
              if (clickTimerRef.current) {
                clearTimeout(clickTimerRef.current);
                clickTimerRef.current = null;
                console.log('Cleared single click timer due to double click.');
              }
              
              const clickedTextElement = e.target.closest('h2, h3, h4, h5, h6, p, blockquote');
              const clickedImageElement = e.target.closest('img');

              if (clickedImageElement && clickedImageElement.closest('.article-content-container')){
                 // --- Handle Image Double Click (Select Image) ---
                 e.stopPropagation();
                 if (!clickedImageElement.id) {
                    console.warn("Double-clicked image missing ID:", clickedImageElement);
                    showAlert("Double-clicked image is missing an ID. Cannot select.", "warning");
                    return;
                 }
                 console.log('Image double-clicked (treated as single select):', clickedImageElement.id);
                 
                 // Clear text selections
                 document.querySelectorAll('.selected, .batch-selected').forEach(el => el.classList.remove('selected', 'batch-selected'));
                 setBatchSelectedSectionIds([]);
                 setSelectedSectionId(null);
                 if (isPopoverOpen) handleClosePopover();
                 
                 // Handle image selection/deselection (same as single click)
                 const currentSelectedImageId = selectedImageElementId;
                 const previouslySelectedImage = currentSelectedImageId ? document.getElementById(currentSelectedImageId) : null;
                 if (previouslySelectedImage) {
                   previouslySelectedImage.classList.remove('selected-image');
                 }
                 
                 if (currentSelectedImageId === clickedImageElement.id) {
                   setSelectedImageElementId(null);
                   setSelectedImageSrc(null);
                 } else {
                   setSelectedImageElementId(clickedImageElement.id);
                   setSelectedImageSrc(clickedImageElement.src);
                   clickedImageElement.classList.add('selected-image');
                 }

              } else if (clickedTextElement) {
                  // --- Handle Text Double Click (Open Popover) --- 
                  e.stopPropagation(); 
                  if (!clickedTextElement.id) {
                     console.warn("Double-clicked element missing ID:", clickedTextElement);
                     showAlert("Double-clicked section is missing an ID. Cannot edit.", "error");
                     return;
                  }
                  const elementId = clickedTextElement.id;
                  console.log('Executing double click for text:', elementId);
                  
                  // Clear image and other text selections
                  document.querySelectorAll('.selected, .batch-selected, .selected-image').forEach(el => {
                      if (el.id !== elementId) {
                          el.classList.remove('selected', 'batch-selected', 'selected-image');
                      }
                  });
                  setBatchSelectedSectionIds([]);
                  setSelectedImageElementId(null);
                  setSelectedImageSrc(null);
                  
                  // Add the single-selection visual for text
                  clickedTextElement.classList.add('selected'); 
                  clickedTextElement.classList.remove('batch-selected'); // Ensure batch is removed
                  
                  // Call handleSectionSelect to set state and open the Popover
                  handleSectionSelect(null, clickedTextElement.innerHTML, elementId); 
            }
          }}
          ref={articleContentRef}
          dangerouslySetInnerHTML={{ __html: editContent }}
        />

        <Box sx={{ 
          mt: 6, 
          pt: 4, 
          borderTop: '1px solid',
          borderColor: alpha('#6B7280', 0.1),
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            mb: 2,
          }}>
            <Chip 
              icon={<LocalOffer />} 
              label="Category" 
              size="small"
              sx={{ 
                backgroundColor: alpha('#6B7280', 0.1),
                color: '#6B7280',
                '& .MuiChip-icon': {
                  color: '#6B7280',
                }
              }}
            />
          </Box>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            color: '#6B7280',
          }}>
            <Button 
              startIcon={<ThumbUp />}
              sx={{ 
                color: '#6B7280',
                '&:hover': {
                  backgroundColor: alpha('#6B7280', 0.08),
                }
              }}
            >
              Like
            </Button>
            <Button 
              startIcon={<Share />}
              sx={{ 
                color: '#6B7280',
                '&:hover': {
                  backgroundColor: alpha('#6B7280', 0.08),
                }
              }}
            >
              Share
            </Button>
            <Button 
              startIcon={<Bookmark />}
              sx={{ 
                color: '#6B7280',
                '&:hover': {
                  backgroundColor: alpha('#6B7280', 0.08),
                }
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Box>
    );
  };

  const logModels = () => {
    console.log('Available models:', availableModels);
    console.log('Current model:', model);
    console.log('Auxiliary model:', getAuxiliaryModel());
  };

  const SaveConfirmDialog = () => (
    <Dialog
      open={saveConfirmOpen}
      onClose={() => setSaveConfirmOpen(false)}
      aria-labelledby="save-dialog-title"
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        }
      }}
    >
      <DialogTitle id="save-dialog-title" sx={{ pb: 1 }}>
        Confirm Save Changes
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          You are about to update a live article on your WordPress site. These changes will be visible to your audience immediately once published.
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          Are you sure you want to save these changes?
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setSaveConfirmOpen(false)} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={handleSavePost} 
          variant="contained"
          color="primary"
          sx={{
            bgcolor: '#6B7280',
            '&:hover': {
              bgcolor: '#4B5563',
            }
          }}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );

  const ensureStableIds = (htmlContent) => {
    if (!htmlContent) return '';
    try {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      const elements = tempDiv.querySelectorAll('h2, h3, h4, h5, h6, p, blockquote');
      elements.forEach((el, index) => {
        if (!el.id || !el.id.startsWith('section-')) {
          el.id = `section-${index}-${Math.random().toString(36).substr(2, 5)}`;
        }
      });
      return tempDiv.innerHTML;
    } catch (error) {
      console.error("Error ensuring stable IDs:", error);
      return htmlContent;
    }
  };

  // Ensure images have IDs when content is set or updated
  const ensureImageIds = (htmlContent) => {
    if (!htmlContent) return '';
    try {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      const images = tempDiv.querySelectorAll('img');
      images.forEach((img, index) => {
        if (!img.id || !img.id.startsWith('img-')) {
          // Add a simple unique ID if missing
          img.id = `img-${index}-${Math.random().toString(36).substr(2, 5)}`;
        }
      });
      return tempDiv.innerHTML;
    } catch (error) {
      console.error("Error ensuring image IDs:", error);
      return htmlContent;
    }
  };

  // --- NEW: Image Handler Placeholder ---
  const handleImageInsert = () => {
    // This function will be called when the toolbar image button is clicked
    console.log("Image button clicked. Opening Image Dialog...");
    // Later: Get cursor position: const cursorIndex = quillRef.current?.getEditor().getSelection()?.index || 0;
    setIsImageDialogOpen(true);
    // The dialog will handle fetching/uploading and calling onImageSelect
  };

  // --- NEW: Callback for Image Selection ---
  const onImageSelect = (url, alt = '') => {
    console.log("DEBUG: onImageSelect called in ArticleRewritesPage", { url, alt, selectedImageElementId });

    if (selectedImageElementId) {
      // Logic for replacing an existing image in the main content preview
      console.log(`DEBUG: Replacing image in main content. Target ID: ${selectedImageElementId}, New URL: ${url}`);
      try {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = editContent;
        const imageToUpdate = tempDiv.querySelector(`img#${selectedImageElementId}`);

        if (imageToUpdate) {
          console.log("DEBUG: Found image element to update.", imageToUpdate);
          imageToUpdate.src = url;
          imageToUpdate.alt = alt; // Update alt text as well
          // Remove the selection class from the actual DOM element if it exists
          const liveImageElement = document.getElementById(selectedImageElementId);
          if (liveImageElement) {
             liveImageElement.classList.remove('selected-image');
          }
          
          // Update the main content state
          const updatedContent = tempDiv.innerHTML;
          setEditContent(updatedContent);
          console.log("DEBUG: Updated editContent state.");
          showAlert('Image replaced successfully!', 'success');
          
          // Clear selection state
          setSelectedImageElementId(null);
          setSelectedImageSrc(null);

        } else {
          console.error(`DEBUG: Could not find image with ID ${selectedImageElementId} in editContent.`);
          showAlert(`Error: Could not find the selected image (ID: ${selectedImageElementId}) to replace.`, 'error');
          // Clear selection state even if update fails
          setSelectedImageElementId(null);
          setSelectedImageSrc(null);
        }
      } catch (error) {
        console.error("DEBUG: Error replacing image in editContent:", error);
        showAlert(`Error replacing image: ${error.message}`, 'error');
        // Clear selection state on error
        setSelectedImageElementId(null);
        setSelectedImageSrc(null);
      }

    } else {
      // Fallback/Old logic (or error if this path shouldn't be reached)
      // This path might be used if we later implement inserting NEW images into the Popover
      console.warn("DEBUG: onImageSelect called without a selectedImageElementId. This might indicate inserting into Popover editor (if implemented) or an unexpected state.");
      // Original Quill logic (kept for potential future use, but likely not for replacement)
      if (quillRef.current) {
        const quill = quillRef.current.getEditor();
        const range = quill.getSelection(true);
        const index = range ? range.index : quill.getLength();
        quill.insertEmbed(index, 'image', url, 'user');
        quill.setSelection(index + 1, 0, 'silent');
        console.log("DEBUG: Fallback - inserted image into Quill editor (Popover).");
      } else {
         console.error("DEBUG: No selected image ID and Quill editor ref is also null.");
         showAlert('Cannot determine where to insert the image.', 'warning');
      }
    }

    setIsImageDialogOpen(false); // Close dialog regardless of success/failure
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        category="Content Tools"
        title="Article Rewrites"
        icon={
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 10px rgba(107, 114, 128, 0.2)',
          }}>
            <FileEditIcon size={24} color="#FFFFFF" strokeWidth={1.5} />
          </Box>
        }
        description="Select and edit articles from your WordPress sites. Use AI to generate new titles and rewrite content."
        noSidePadding={true}
      />

      {!selectedPost ? (
        <Paper
          elevation={0}
          sx={{
            p: 0,
            borderRadius: '16px',
            border: '1px solid',
            borderColor: alpha('#6B7280', 0.1),
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)',
            background: 'linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(245,247,250,0.9) 100%)',
            backdropFilter: 'blur(10px)',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ 
            p: 3, 
            borderBottom: '1px solid',
            borderColor: alpha('#6B7280', 0.08),
            background: 'linear-gradient(to right, rgba(255,255,255,0.9), rgba(249,250,251,0.9))',
          }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#111827' }}>
              Select an Article to Rewrite
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>WordPress Site</InputLabel>
                  <Select
                    value={selectedSite}
                    onChange={(e) => setSelectedSite(e.target.value)}
                    label="WordPress Site"
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#6B7280', 0.2),
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#6B7280', 0.3),
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3B82F6',
                      },
                      borderRadius: '10px',
                    }}
                  >
                    {sites.map((site) => (
                      <MenuItem key={site.id} value={site.id}>
                        {site.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    label="Category"
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#6B7280', 0.2),
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#6B7280', 0.3),
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3B82F6',
                      },
                      borderRadius: '10px',
                    }}
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Search Articles"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                    sx: {
                      borderRadius: '10px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#6B7280', 0.2),
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#6B7280', 0.3),
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3B82F6',
                      },
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {error && (
            <Alert severity="error" sx={{ m: 3, borderRadius: '10px' }}>
              {error}
            </Alert>
          )}

          <Box sx={{ p: 3 }}>
            {loading ? (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                py: 8,
                flexDirection: 'column',
                gap: 2
              }}>
                <CircularProgress size={40} sx={{ color: '#3B82F6' }} />
                <Typography variant="body2" color="text.secondary">
                  Loading articles...
                </Typography>
              </Box>
            ) : (
              <>
                {posts.length === 0 ? (
                  <Box sx={{ 
                    py: 8, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 2,
                    color: '#6B7280'
                  }}>
                    <FileEditIcon size={40} strokeWidth={1.5} />
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      No articles found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '400px', textAlign: 'center' }}>
                      Try changing your filters or search term to find articles to rewrite.
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {posts.map((post) => (
                      <Grid item xs={12} key={post.id}>
                        <Card
                          elevation={0}
                          sx={{
                            borderRadius: '12px',
                            border: '1px solid',
                            borderColor: alpha('#6B7280', 0.1),
                            transition: 'all 0.2s ease-in-out',
                            position: 'relative',
                            overflow: 'hidden',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
                              borderColor: alpha('#6B7280', 0.2),
                              '& .edit-button': {
                                opacity: 1,
                                transform: 'translateX(0)',
                              },
                              '&::after': {
                                opacity: 1,
                              }
                            },
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'linear-gradient(90deg, transparent 0%, rgba(59, 130, 246, 0.02) 100%)',
                              opacity: 0,
                              transition: 'opacity 0.3s ease',
                              pointerEvents: 'none',
                            }
                          }}
                        >
                          <CardContent sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, p: 3 }}>
                            <Box sx={{ 
                              width: { xs: '100%', sm: '140px' }, 
                              minWidth: { xs: 'auto', sm: '140px' },
                              height: { xs: '180px', sm: '100px' },
                              borderRadius: '8px',
                              overflow: 'hidden',
                              mb: { xs: 2, sm: 0 },
                              bgcolor: alpha('#f1f5f9', 0.8),
                              border: '1px solid',
                              borderColor: alpha('#6B7280', 0.1),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              position: 'relative'
                            }}>
                              {(post.featured_media_url || post.featured_image_url || 
                                (post._embedded && post._embedded['wp:featuredmedia'] && 
                                post._embedded['wp:featuredmedia'][0] && 
                                post._embedded['wp:featuredmedia'][0].source_url)) ? (
                                <img 
                                  src={post.featured_media_url || post.featured_image_url || 
                                      (post._embedded && post._embedded['wp:featuredmedia'] && 
                                      post._embedded['wp:featuredmedia'][0] && 
                                      post._embedded['wp:featuredmedia'][0].source_url)}
                                  alt={post.title.rendered}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                  }}
                                  onError={(e) => {
                                    console.error('Image failed to load:', e.target.src);
                                    e.target.onerror = null;
                                    e.target.style.display = 'none';
                                    e.target.parentNode.classList.add('image-error');
                                  }}
                                />
                              ) : (
                                <Box sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  color: alpha('#6B7280', 0.7),
                                }}>
                                  <ImageIcon sx={{ fontSize: '2rem', mb: 0.5 }} />
                                  <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>No image</Typography>
                                </Box>
                              )}
                            </Box>
                            
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: 2 }}>
                                <Typography 
                                  variant="h6" 
                                  gutterBottom 
                                  sx={{ 
                                    fontWeight: 600, 
                                    color: '#111827',
                                    mb: 0.5,
                                    lineHeight: 1.3,
                                    fontSize: '1.1rem'
                                  }}
                                  dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                                />
                              </Box>
                              
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#6B7280', fontSize: '0.85rem' }}>
                                  <CalendarToday sx={{ fontSize: '0.9rem' }} />
                                  <Typography variant="caption">
                                    {new Date(post.date).toLocaleDateString('en-US', { 
                                      year: 'numeric', 
                                      month: 'short', 
                                      day: 'numeric' 
                                    })}
                                  </Typography>
                                </Box>
                                
                                {post.categories && post.categories.length > 0 && (
                                  <Chip 
                                    label="Article" 
                                    size="small" 
                                    sx={{ 
                                      bgcolor: alpha('#3B82F6', 0.1),
                                      color: '#3B82F6',
                                      fontWeight: 500,
                                      fontSize: '0.7rem',
                                      height: '22px'
                                    }} 
                                  />
                                )}
                              </Box>
                              
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ 
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  mb: 1.5,
                                  fontSize: '0.9rem',
                                  lineHeight: 1.5,
                                  maxWidth: '90%'
                                }}
                              >
                                {post.excerpt.rendered.replace(/<[^>]*>/g, '').substring(0, 180)}...
                              </Typography>
                              
                              <Button
                                className="edit-button"
                                startIcon={<Edit />}
                                onClick={() => handleEditPost(post)}
                                sx={{
                                  color: '#3B82F6',
                                  backgroundColor: alpha('#3B82F6', 0.05),
                                  '&:hover': {
                                    backgroundColor: alpha('#3B82F6', 0.1),
                                  },
                                  textTransform: 'none',
                                  fontWeight: 500,
                                  borderRadius: '8px',
                                  transition: 'all 0.3s ease',
                                  opacity: 0.9,
                                  transform: { xs: 'none', sm: 'translateX(-5px)' },
                                  mt: { xs: 1, sm: 0 }
                                }}
                              >
                                Edit & Rewrite
                              </Button>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}

                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={(e, value) => setCurrentPage(value)}
                      color="primary"
                      sx={{
                        '& .MuiPaginationItem-root': {
                          borderRadius: '8px',
                          '&.Mui-selected': {
                            backgroundColor: alpha('#3B82F6', 0.1),
                            color: '#3B82F6',
                            fontWeight: 600,
                            '&:hover': {
                              backgroundColor: alpha('#3B82F6', 0.2),
                            }
                          },
                          '&:hover': {
                            backgroundColor: alpha('#3B82F6', 0.05),
                          }
                        }
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px - 24px)' }}>
            <Paper
            elevation={1} 
              sx={{
              p: 1.5, 
              mb: 2, 
              borderRadius: '12px', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              zIndex: 1100,
              background: 'rgba(255, 255, 255, 0.85)',
              backdropFilter: 'blur(8px)',
              flexWrap: 'wrap',
              gap: 1
            }}
          >
                      <Button
                        startIcon={<ChevronRight sx={{ transform: 'rotate(180deg)' }} />}
                        onClick={() => setSelectedPost(null)}
              disabled={isBatchRewriting}
                        sx={{
                color: alpha('#6B7280', 0.9),
                          fontWeight: 500,
                fontSize: '0.9rem',
                        }}
                      >
                        Back to Articles
                      </Button>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              {/* --- DEBUG LOG --- */} 
              {console.log("Rendering Action Bar - selectedImageSrc:", selectedImageSrc)}
              {/* Conditionally render image replace buttons */} 
              {selectedImageSrc && (
                <>
                                <Button
                    variant="outlined"
                                  size="small"
                    onClick={() => setIsImageDialogOpen(true)} // Opens the library dialog
                    sx={{ borderColor: '#f59e0b', color: '#f59e0b' }}
                  >
                    Replace Image
                                </Button>
                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                </>
              )}

              {/* Existing buttons: Batch Rewrite, Reset, Save */} 
              <Tooltip title={batchSelectedSectionIds.length > 0 ? `Rewrite ${batchSelectedSectionIds.length} selected section(s) sequentially` : "Shift+Click sections to select multiple"}>
                <span>
                                <Button
                      variant="outlined"
                      startIcon={isBatchRewriting ? <CircularProgress size={16} color="inherit" /> : <PlaylistPlayIcon size={18} />}
                      onClick={handleBatchRewriteSelectedSections}
                      disabled={batchSelectedSectionIds.length === 0 || isBatchRewriting || loading || isRewritingSection}
                                  sx={{ 
                          color: '#10B981',
                          borderColor: alpha('#10B981', 0.5),
                          borderRadius: '8px',
                                    '&:hover': {
                              borderColor: '#10B981',
                              backgroundColor: alpha('#10B981', 0.05),
                          },
                          '&.Mui-disabled': {
                              color: alpha('#10B981', 0.5),
                              borderColor: alpha('#10B981', 0.2),
                          }
                      }}
                  >
                      {isBatchRewriting ? `Rewriting... (${Math.round(batchRewriteProgress)}%)` : `Rewrite Selected (${batchSelectedSectionIds.length})`}
                                </Button>
                </span>
              </Tooltip>
                                <Button
                  variant="outlined"
                  startIcon={<RefreshCwIcon size={16} />}
                  onClick={handleResetArticle}
                  disabled={loading || isBatchRewriting || isRewritingSection}
                                  sx={{ 
                      borderColor: alpha('#6B7280', 0.4),
                      color: '#6B7280',
                      borderRadius: '8px',
                                    '&:hover': {
                          borderColor: '#6B7280',
                          backgroundColor: alpha('#6B7280', 0.05),
                                    }
                                  }}
                                >
                  Reset
                                </Button>
                                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSaveConfirm}
                  disabled={loading || isBatchRewriting || isRewritingSection}
                                  sx={{ 
                      backgroundColor: '#6B7280',
                      borderRadius: '8px',
                                    '&:hover': {
                          backgroundColor: '#4B5563',
                                    }
                                  }}
                                >
                  Save Changes
                            </Button>
                          </Box>
          </Paper>
                          
                            <Box 
                              sx={{ 
                  flexGrow: 1, 
                  overflowY: 'auto',
                  pr: 1,
                  position: 'relative',
                  transition: 'opacity 0.3s ease',
              }}
          >
              <Paper
                  elevation={0}
                                      sx={{ 
                      borderRadius: '16px', 
                              border: '1px solid',
                      borderColor: alpha('#6B7280', 0.1),
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)',
                      background: 'linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(245,247,250,0.9) 100%)',
                      backdropFilter: 'blur(10px)',
                      overflow: 'visible',
                      mb: 3
                  }}
              >
                  {renderArticlePreview()}
              </Paper>

              <Backdrop
                                      sx={{ 
                  color: '#fff', 
                  position: 'absolute', 
                  zIndex: (theme) => theme.zIndex.drawer + 1,
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  borderRadius: '16px'
                }}
                open={isBatchRewriting}
              >
                <Box 
                                      sx={{ 
                                display: 'flex', 
                    flexDirection: 'column', 
                                alignItems: 'center', 
                    gap: 2 
                  }}
                >
                  <CircularProgress color="inherit" />
                  <Typography variant="h6">{batchRewriteStatusMessage}</Typography>
                  <Box sx={{ width: '80%', maxWidth: '400px' }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={batchRewriteProgress} 
                                      color="inherit"
                      sx={{ height: '8px', borderRadius: '4px' }}
                    />
                                  </Box>
                              </Box>
              </Backdrop>

                                </Box>
                              </Box>
      )}

      <SaveConfirmDialog />
      
      <Popover
        open={isPopoverOpen}
        anchorEl={popoverAnchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
                                      }}
                                      sx={{ 
          marginLeft: 2
        }}
        PaperProps={{
          sx: {
            p: 2.5,
            width: '380px',
            minHeight: '480px',
            borderRadius: '14px',
            boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
            border: `1px solid ${alpha('#9333ea', 0.4)}`,
            background: '#ffffff',
            opacity: isBatchRewriting ? 0.5 : 1,
            pointerEvents: isBatchRewriting ? 'none' : 'auto',
            overflow: 'visible',
                                display: 'flex',
            flexDirection: 'column',
            '& .ql-editor': {
              fontSize: '1rem',
              lineHeight: 1.6,
            },
            '& .ql-container': {
              flexGrow: 1,
                                  display: 'flex', 
              flexDirection: 'column',
              overflow: 'auto'
            },
            '& .ql-editor': {
              flexGrow: 1,
              overflow: 'auto',
              height: '100%'
            }
          }
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#9333ea', flexShrink: 0 }}> 
          Edit Section
                          </Typography>
        <Box sx={{ flexGrow: 1, position: 'relative', mb: 2, display: 'flex' }}> 
          {inlineEditorHtml != null ? (
            <> {/* Add fragment for logging */} 
              {/* {console.log("DEBUG: Rendering ReactQuill, quillRef.current:", quillRef.current)} <-- REMOVED LOG */} 
              <ReactQuill 
                ref={quillRef} // Assign the ref
                theme="snow" 
                value={inlineEditorHtml}
                onChange={setInlineEditorHtml}
                readOnly={isRewritingSection}
                style={{
                  backgroundColor: isRewritingSection ? alpha('#f3e8ff', 0.5) : 'white',
                  borderRadius: '10px',
                  border: `1px solid ${alpha('#9333ea', 0.2)}`,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  flexGrow: 1,
                }}
                modules={{
                  toolbar: {
                    container: [
                      [{'header': [1, 2, 3, false]}],       
                      ['bold', 'italic', 'underline','strike'],      
                      [{'list': 'ordered'}, {'list': 'bullet'}],
                      ['link'], // Image button removed
                      ['clean']                                         
                    ],
                    // handlers: { // Remove image handler
                    //  'image': handleImageInsert 
                    // }
                  }
                }}
              />
            </>
           ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
              <CircularProgress />
                                  </Box>
                                )}
                        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                      <Button
                        variant="outlined"
            size="small"
                                    startIcon={<AutoFixHigh />}
                                    onClick={handleRewriteSection}
            disabled={isRewritingSection || isBatchRewriting}
                        sx={{
              color: '#7e22ce',
              borderColor: alpha('#9333ea', 0.5),
                                      borderRadius: '8px',
                          '&:hover': {
                borderColor: '#9333ea',
                backgroundColor: alpha('#9333ea', 0.05),
              },
            }}
          >
            {isRewritingSection ? 'Rewriting...' : 'Rewrite with AI'}
                      </Button>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <Button
              onClick={handleClosePopover} 
              color="inherit" 
              size="small"
              sx={{ borderRadius: '8px' }}
              disabled={isBatchRewriting}
            >
               Cancel
                                    </Button>
                          <Button
                        variant="contained"
               size="small"
                        startIcon={<Save />}
               onClick={applyInlineSectionChanges} 
               disabled={isRewritingSection || !inlineEditorHtml || isBatchRewriting}
                        sx={{
                 bgcolor: '#9333ea',
                              borderRadius: '8px',
                 '&:hover': { bgcolor: '#7e22ce' },
                 '&.Mui-disabled': { 
                   bgcolor: alpha('#9333ea', 0.4),
                   color: alpha('#ffffff', 0.7) 
                 }
               }}
             >
               Apply
                      </Button>
                </Box>
        </Box>
      </Popover>

      <ImageSelectionDialog 
        open={isImageDialogOpen}
        onClose={() => setIsImageDialogOpen(false)}
        onImageSelect={onImageSelect} 
        siteId={selectedSite}
        authenticatedRequest={authenticatedRequest}
      />

    </Box>
  );
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`editor-tabpanel-${index}`}
      aria-labelledby={`editor-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default ArticleRewritesPage;